import { z } from 'zod';

/**
 * One **eligible Unit** of the order (`GET /devices/admin/order-devices?orderId=`). Flat — one row
 * per Unit, not warranty's nested `eligibleUnits[]`.
 *
 * The response is the **sole eligibility oracle** (ADR-0024): order status, cancellation, approved
 * replacements, bundle-component inheritance, already-configured and DM-Devices-only are all applied
 * server-side and are never recomputed here. A cancelled order simply returns `[]`.
 *
 * No device status and no display name are carried (`context.md` #19), so `productName` is the label
 * source. `quantity` is the line item's **total**, which is what makes the shared Unit label suffix
 * only multi-unit lines. `bundleDeviceId` is present only for a device inside a Bundle and is what
 * makes a Bundle's components individually addressable.
 */
export const orderDeviceSchema = z.object({
  orderItemId: z.string(),
  productName: z.string(),
  quantity: z.number(),
  unitIndex: z.number(),
  bundleDeviceId: z.string().nullish(),
});

export const orderDevicesSchema = z.array(orderDeviceSchema);

/**
 * `POST /devices/admin/verify-serial` — Digital Matter's verdict on one serial. The request carries the
 * **serial alone** and this call answers only "does Digital Matter know this serial"; why, and where
 * the other failure mode went, is recorded in ADR-0024's 2026-08-05 amendment.
 *
 * Only `valid` is declared because only `valid` is read — a schema key nothing consumes is as inert as
 * a schema nothing parses (the rule ticket 03's review applied). A zod object strips unknown keys, so
 * the FE-proposed `lastAppliedTemplateId` rides along untouched.
 */
export const verifySerialSchema = z.object({
  valid: z.boolean(),
});

/**
 * `GET /devices/admin/product-types` — the **DM Category** list. A flat array of Digital Matter product
 * types with no ids (`["G62","Hawk4G","Oyster34G",…]`), which is exactly why DM Category has no separate
 * identity: it *is* the `productName` already carried on every eligible-unit row (ADR-0024, grilling Q4).
 */
export const productTypesSchema = z.array(z.string());

/**
 * One Digital Matter **Parameter Template** for a product type
 * (`GET /devices/admin/param-templates?productName=`). `templateId` is a number on the wire and a string
 * in the form — the same string-in-RHF/number-on-the-wire split `serialNumber` uses, so the api boundary
 * does the one `Number()`.
 *
 * `Description`, `ProductId` and `Type` also come back from Digital Matter and are **not** declared: a
 * schema key nothing consumes is as inert as a schema nothing parses (the rule ticket 03's review
 * applied), and a zod object strips unknown keys, so they ride along untouched.
 */
export const paramTemplateSchema = z.object({
  templateId: z.number(),
  name: z.string(),
});

export const paramTemplatesSchema = z.array(paramTemplateSchema);

/**
 * `POST /devices/admin/param-templates/set` — the **first** of the apply's two calls. It writes the
 * template to the device and leaves it in **Pending Confirmation**; `param-templates/confirm` is what
 * reaches Pending QC (ticket 13).
 *
 * `customerDeviceId` is the one key declared, and unlike the one-call apply this replaces — which
 * parsed nothing — it has to be: it is the confirmation call's whole body, and AC2 requires it come
 * from here rather than from anything the frontend reconstructed. The customer-device row may not have
 * existed before this call created it, so there is nothing to reconstruct it from.
 *
 * `dmDeviceDetail` also comes back — the live Digital Matter values ticket 14 reads for Preview — and
 * is deliberately **not** declared yet: a schema key nothing consumes is as inert as a schema nothing
 * parses (the rule ticket 03's review applied), and a zod object strips unknown keys, so it rides
 * along untouched until ticket 14 needs it.
 */
export const setParamTemplateSchema = z.object({
  customerDeviceId: z.string(),
});

/*
 * `POST /devices/admin/param-templates/confirm` gets **no** schema. It answers
 * `{customerDeviceId, status: 'pending_qc'}` and neither key is read: the status the admin sees is
 * re-fetched from the Customer Devices table, and the id is one we just sent. A schema nothing parses
 * is an inert declaration (the rule ticket 03's review applied). Its request body is typed inline at
 * the api boundary, as `verify-serial`'s is.
 */

/**
 * The one form that spans all three steps, with each step's fields `pick`ed out of it rather than
 * declared twice. RHF's `shouldUnregister: false` default is what makes "Previous preserves my data"
 * free rather than coded (ADR-0024).
 *
 * `device` is the composite `${orderItemId}#${unitIndex}#${bundleDeviceId ?? ''}` option value from
 * `@/lib/orders`, split apart at the api boundary by whichever ticket first posts it.
 *
 * `serialNumber` and `templateId` are **strings** here and numbers on the wire: the serial input
 * sanitises text as the admin types, Digital Matter treats serials as integers (`{"Devices":[int,…]}`),
 * and a string option value keeps both selects on the shared `<Select>` with no per-field coercion. The
 * 5–7 bound is the AC's, not Figma's, whose drawn serial is 8 digits (FLAG-BA-6).
 *
 * `productName` is the chosen **DM Category** — preselected from the device on a successful serial
 * verification and still changeable (AC2). Its compatibility with the device is **not** expressed here:
 * the comparison needs the device's own product type, which lives in the eligible-units response rather
 * than in the form, so it is derived in the component (AC7/AC8).
 */
export const addParametersSchema = z.object({
  device: z.string().min(1, 'Select a device'),
  serialNumber: z.string().regex(/^\d{5,7}$/, 'Serial Number must be 5-7 digits'),
  productName: z.string().min(1, 'Select a DM category'),
  templateId: z.string().min(1, 'Select a parameter template'),
});
